import {
	AdAccountDTO,
	BingAdsUetTagDTO,
	FacebookAdsCampaignDTO,
	GoogleAdsAdAccountDTO,
	GoogleAdsCampaignDTO,
	GoogleAdsInfoDTO,
	ProtectionInfoDTO
} from 'api-models'
import { toQuery } from 'utils'
import { ApiClient } from '../api-client'

async function find(
	productAccountId: string,
	query?: Record<string, string>
): Promise<Array<AdAccountDTO>> {
	const { data } = await ApiClient.client.get(
		`/accounts/${productAccountId}/ad-accounts?${toQuery(query)}`
	)
	return data.data.filter(
		(adAccount: AdAccountDTO) => adAccount.status !== 'DELETED'
	)
}

async function getById(
	productAccountId: string,
	id: string
): Promise<AdAccountDTO> {
	const { data } = await ApiClient.client.get(
		`/accounts/${productAccountId}/ad-accounts/${id}`
	)
	return data.data
}

async function createGoogleAdsAccount(
	productAccountId: string,
	payload: {
		account_number: string
		link_id?: string | null
		asset_id?: string | null
	}
): Promise<AdAccountDTO> {
	const { data } = await ApiClient.client.post(
		`/accounts/${productAccountId}/ad-accounts/google-ads`,
		payload
	)
	return data.data
}
const createPPCGoogleAdsAccount = async (
	accountId: string,
	payload: {
		asset_id: string
		link_id: string // account link ID
		account_number: string
		set_tracking_template: boolean
	}
): Promise<GoogleAdsAdAccountDTO> => {
	try {
		const { data } = await ApiClient.client.post(
			`/accounts/${accountId}/ad-accounts/gads`,
			payload
		)
		return data.data
	} catch (err) {
		return Promise.reject(err)
	}
}

const createPPCFacebookAdsAccount = async (
	accountId: string,
	payload: {
		asset_id: string
		link_id: string // account link ID
		account_number: string
		set_custom_audience: boolean
	}
): Promise<GoogleAdsCampaignDTO[]> => {
	try {
		const { data } = await ApiClient.client.post(
			`/accounts/${accountId}/ad-accounts/fads`,
			payload
		)
		return data.data
	} catch (err) {
		return Promise.reject(err)
	}
}

const createPPCBingAdsAccount = async (
	accountId: string,
	payload: {
		asset_id: string
		link_id: string // account link ID
		account_number: string
		customer_id: string
		ad_account_id: string
	}
): Promise<void> => {
	try {
		const { data } = await ApiClient.client.post(
			`/accounts/${accountId}/ad-accounts/bads`,
			payload
		)
		return data.data
	} catch (err) {
		return Promise.reject(err)
	}
}

const sync = async (
	accountId: string | undefined,
	adAccountId: string,
	platform: string
): Promise<AdAccountDTO[]> => {
	const { data } = await ApiClient.client.post(
		`/accounts/${accountId}/ad-accounts/${platform}/${adAccountId}/sync`,
		{}
	)
	return data.data
}

const resyncTracking = async (
	accountId: string | undefined,
	adAccountId: string
): Promise<void> => {
	await ApiClient.client.post(
		`/accounts/${accountId}/ad-accounts/${adAccountId}/tracking/resync`,
		{}
	)
}

export type VerifyTrackingResult = {
	verified: boolean
	trackingTemplateMatch: boolean
	urlSuffixMatch: boolean
	currentTrackingTemplate: string | null
	currentUrlSuffix: string | null
	expectedSuffix: string | null
	expectedTemplate: string | null
}

const verifyTracking = async (
	accountId: string | undefined,
	assetId: string | undefined,
	adAccountId: string
): Promise<VerifyTrackingResult> => {
	const { data } = await ApiClient.client.post(
		`/setup/${accountId}/${assetId}/ad-account/${adAccountId}/verify-tracking`
	)
	return data.data
}

const setAdAccountProtection = async (
	accountId: string | undefined,
	adAccountId: string | undefined,
	payload: ProtectionInfoDTO
): Promise<AdAccountDTO[]> => {
	const { data } = await ApiClient.client.patch(
		`/accounts/${accountId}/ad-accounts/${adAccountId}/protection`,
		payload
	)
	return data.data
}
const setCampaignProtection = async (
	accountId: string,
	adAccountId: string,
	campaignId: string,
	payload: ProtectionInfoDTO,
	platform?: AdAccountDTO['platform']
): Promise<GoogleAdsCampaignDTO | FacebookAdsCampaignDTO> => {
	const { data } = await ApiClient.client.patch(
		`/accounts/${accountId}/ad-accounts/${adAccountId}/campaigns/${campaignId}/protection?platform=${platform}`,
		payload
	)
	return data.data
}
const getGoogleCampaigns = async (
	accountId: string,
	adAccountId: string
): Promise<GoogleAdsCampaignDTO[]> => {
	const { data } = await ApiClient.client.get(
		`/accounts/${accountId}/ad-accounts/gads/${adAccountId}/campaigns`
	)
	return data.data
}
const getFacebookCampaigns = async (
	accountId: string,
	adAccountId: string
): Promise<FacebookAdsCampaignDTO[]> => {
	const { data } = await ApiClient.client.get(
		`/accounts/${accountId}/ad-accounts/fads/${adAccountId}/campaigns`
	)
	return data.data
}
const getBingCampaigns = async (
	accountId: string,
	adAccountId: string
): Promise<FacebookAdsCampaignDTO[]> => {
	const { data } = await ApiClient.client.get(
		`/accounts/${accountId}/ad-accounts/bads/${adAccountId}/campaigns`
	)
	return data.data
}
const updateTrackingToggles = async (
	accountId: string,
	adAccountId: string,
	platform: AdAccountDTO['platform'],
	payload: {
		set_tracking_template: boolean
		automated_tracking: boolean
	}
): Promise<GoogleAdsCampaignDTO[]> => {
	const { data } = await ApiClient.client.patch(
		`/accounts/${accountId}/ad-accounts/${
			platform === 'BADS' ? 'bads' : 'gads'
		}/${adAccountId}/tracking`,
		payload
	)
	return data.data
}
const updateGadsSettingsAccessLink = async (
	accountId: string,
	adAccountId: string,
	payload: { link_id: string }
): Promise<void> => {
	const { data } = await ApiClient.client.patch(
		`/accounts/${accountId}/ad-accounts/gads/${adAccountId}/link`,
		payload
	)
	return data.data
}
const updateFadsSettingsAccessLink = async (
	accountId: string,
	adAccountId: string,
	payload: { link_id: string }
): Promise<void> => {
	const { data } = await ApiClient.client.patch(
		`/accounts/${accountId}/ad-accounts/fads/${adAccountId}/link`,
		payload
	)
	return data.data
}
const updateBadsSettingsAccessLink = async (
	accountId: string,
	adAccountId: string,
	payload: { link_id: string }
): Promise<void> => {
	const { data } = await ApiClient.client.patch(
		`/accounts/${accountId}/ad-accounts/bads/${adAccountId}/link`,
		payload
	)
	return data.data
}
const updateSettingsAccountProtection = async (
	accountId: string,
	adAccountId: string,
	payload: { active: boolean }
): Promise<GoogleAdsCampaignDTO[]> => {
	const { data } = await ApiClient.client.post(
		`/accounts/${accountId}/ad-accounts/${adAccountId}/toggle-active`,
		payload
	)
	return data.data
}
/**
 * DEV-2422 §2.3 — per-campaign click detection, on every platform.
 *
 * Its own endpoint rather than the per-platform settings PATCH: those each demand their
 * whole payload (`set_custom_audience`, `exclusion_period`, `pixel_id`, ...), which a
 * row-level toggle has no business supplying.
 */
const updateIgnoredCampaigns = async (
	accountId: string,
	adAccountId: string,
	ignoredCampaigns: string[]
): Promise<void> => {
	const { data } = await ApiClient.client.patch(
		`/accounts/${accountId}/ad-accounts/${adAccountId}/ignored-campaigns`,
		{ ignored_campaigns: ignoredCampaigns }
	)
	return data.data
}
const updateGadsAdAccountSettings = async (
	accountId: string,
	adAccountId: string,
	payload: {
		name: string
		color: string
		currency: string
		timezone: string
		// Omitted, never `[]`: the service does `data.ignored_campaigns || stored`, and an
		// empty array is truthy, so sending one wipes what the campaign toggle wrote.
		ignored_campaigns?: string[]
		click_tracking: string
		ip_exclusions?: boolean
		audience_exclusions?: boolean
	}
): Promise<GoogleAdsCampaignDTO[]> => {
	const { data } = await ApiClient.client.patch(
		`/accounts/${accountId}/ad-accounts/gads/${adAccountId}/settings`,
		payload
	)
	return data.data
}
const updateFadsAdAccountSettings = async (
	accountId: string,
	adAccountId: string,
	payload: {
		name: string
		color: string
		currency: string
		timezone: string
		set_custom_audience: boolean
	}
): Promise<GoogleAdsCampaignDTO[]> => {
	const { data } = await ApiClient.client.patch(
		`/accounts/${accountId}/ad-accounts/fads/${adAccountId}/settings`,
		payload
	)
	return data.data
}
const updateBadsAdAccountSettings = async (
	accountId: string,
	adAccountId: string,
	payload: {
		name: string
		color: string
		currency: string
		timezone: string
		set_custom_audience: boolean
	}
): Promise<GoogleAdsCampaignDTO[]> => {
	const { data } = await ApiClient.client.patch(
		`/accounts/${accountId}/ad-accounts/bads/${adAccountId}/settings`,
		payload
	)
	return data.data
}
const deleteAdAccountAccount = async (
	accountId: string,
	adAccountId: string
): Promise<GoogleAdsCampaignDTO[]> => {
	const { data } = await ApiClient.client.delete(
		`/accounts/${accountId}/ad-accounts/${adAccountId}`
	)
	return data.data
}

const getAccountAdPixels = async (
	accountId: string,
	adAccountId: string
): Promise<string[]> => {
	const { data } = await ApiClient.client.get(
		`/accounts/${accountId}/ad-accounts/fads/${adAccountId}/ad-pixels`
	)
	return data.data
}

const getBingUetTags = async (
	accountId: string,
	adAccountId: string
): Promise<BingAdsUetTagDTO[]> => {
	const { data } = await ApiClient.client.get(
		`/accounts/${accountId}/ad-accounts/bads/${adAccountId}/uet-tags`
	)
	return data.data
}

const sendMccLinkRequest = async (
	accountId: string,
	adAccountId: string
): Promise<BingAdsUetTagDTO[]> => {
	const { data } = await ApiClient.client.post(
		`/accounts/${accountId}/ad-accounts/gads/${adAccountId}/send-invite`
	)
	return data.data
}
/**
 * DEV-2358 §3.1 — re-read the MCC link status for one account, live from Google,
 * and return it. Backs the Refresh button on the Refunds Agent setup step.
 *
 * Not `sync()`: that queues a full account resync and answers before the worker
 * has run, so the caller would re-read the status it already had.
 */
const refreshMccStatus = async (
	accountId: string,
	adAccountId: string
): Promise<GoogleAdsInfoDTO['mcc_support_connection']> => {
	const { data } = await ApiClient.client.post(
		`/accounts/${accountId}/ad-accounts/gads/${adAccountId}/mcc-status`
	)
	return data.data.mcc_support_connection
}

const sendMccLinkRequests = async (
	accountId: string
): Promise<BingAdsUetTagDTO[]> => {
	const { data } = await ApiClient.client.post(
		`/accounts/${accountId}/ad-accounts/gads/invite-all`
	)
	return data.data
}

const skipMccSupport = async (
	accountId: string,
	adAccountId: string
): Promise<BingAdsUetTagDTO[]> => {
	const { data } = await ApiClient.client.post(
		`/accounts/${accountId}/ad-accounts/gads/${adAccountId}/skip-mcc-support`
	)
	return data.data
}

// DEV-2353 — dismiss MCC banner(s) without changing lifecycle (sets mcc_banner_dismissed_at).
const dismissMccBanner = async (
	accountId: string,
	adAccountIds?: string[]
): Promise<BingAdsUetTagDTO[]> => {
	const { data } = await ApiClient.client.post(
		`/accounts/${accountId}/ad-accounts/gads/dismiss-mcc-banner`,
		{ adAccountIds }
	)
	return data.data
}

export const AdAccounts = {
	find,
	getById,
	createGoogleAdsAccount,
	sendMccLinkRequests,
	refreshMccStatus,
	skipMccSupport,
	dismissMccBanner,
	sendMccLinkRequest,
	createPPCGoogleAdsAccount,
	createPPCFacebookAdsAccount,
	createPPCBingAdsAccount,
	sync,
	resyncTracking,
	verifyTracking,
	setAdAccountProtection,
	setCampaignProtection,
	getGoogleCampaigns,
	getFacebookCampaigns,
	getBingCampaigns,
	updateTrackingToggles,
	updateGadsSettingsAccessLink,
	updateFadsSettingsAccessLink,
	updateBadsSettingsAccessLink,
	updateSettingsAccountProtection,
	updateIgnoredCampaigns,
	updateGadsAdAccountSettings,
	updateFadsAdAccountSettings,
	updateBadsAdAccountSettings,
	deleteAdAccountAccount,
	getAccountAdPixels,
	getBingUetTags
}
